import { Form, type FormInstance, Modal } from "antd";
import type { ReactNode } from "react";
import { useEffect } from "react";

/**
 * Antd `setFieldsValue` `RecursivePartial<T>` kutadi. `Partial<T>` generik `T` uchun
 * unga mos kelmaydi, shuning uchun tipni cast qilish o'rniga formadan olamiz.
 */
type FormModalValues<T> = Parameters<FormInstance<T>["setFieldsValue"]>[0];

interface FormModalProps<T> {
	open: boolean;
	title: string;
	okText?: string;
	/** Kengroq forma uchun (masalan xarita bilan). Standart — Antd standarti. */
	width?: number;
	initialValues?: FormModalValues<T>;
	isSubmitting?: boolean;
	onCancel: () => void;
	onSubmit: (values: T) => void;
	children: ReactNode;
	/**
	 * ⚠️ Ixtiyoriy — CHAQIRUVCHI `Form.useForm()` bilan yaratgan instansiya.
	 * Berilmasa `FormModal` o'zinikini yaratadi (eski xatti-harakat).
	 * `DepartmentFormModal` buni beradi: sovuq keshda daraja katalogi
	 * KECHIKIB kelganda (`suggestedGradeId` o'zgarganda) forma instansiyasiga
	 * to'g'ridan-to'g'ri `setFieldValue` chaqirishi kerak — usiz taklifni
	 * qo'yishning yagona yo'li `initialValues` havolasini o'zgartirish edi,
	 * bu esa PASTDAGI effekt orqali `resetFields()` chaqirib, operator
	 * ALLAQACHON kiritgan boshqa maydonlarni (masalan "Nomi") o'chirardi.
	 */
	form?: FormInstance<T>;
}

/**
 * Antd `Form` + `Modal` qobig'i.
 *
 * `initialValues` o'zgarganda formani qayta to'ldiradi — Antd buni o'zi qilmaydi
 * va tahrirlash modalida oldingi yozuvning qiymatlari qolib ketadi.
 */
export function FormModal<T extends object>({
	open,
	title,
	okText = "Saqlash",
	width,
	initialValues,
	isSubmitting = false,
	onCancel,
	onSubmit,
	children,
	form: externalForm,
}: FormModalProps<T>) {
	const [internalForm] = Form.useForm<T>();
	const form = externalForm ?? internalForm;

	useEffect(() => {
		if (open) {
			form.resetFields();
			if (initialValues) {
				form.setFieldsValue(initialValues);
			}
		}
	}, [open, initialValues, form]);

	return (
		<Modal
			open={open}
			title={title}
			okText={okText}
			width={width}
			cancelText="Bekor qilish"
			confirmLoading={isSubmitting}
			onCancel={onCancel}
			onOk={() => form.submit()}
			destroyOnHidden
		>
			<Form form={form} layout="vertical" requiredMark={false} onFinish={onSubmit}>
				{children}
			</Form>
		</Modal>
	);
}
